import React, { useState } from "react";
import axios from "axios";
import { Button, CircularProgress } from "@material-ui/core";
import { GetApp } from "@material-ui/icons";
import { saveAs } from "file-saver";

import { API_URLS } from "../../constants/api";

// Drop-in "Xuất Excel" button for every Management table. Posts the filtered
// IDs to the BE which renders the full work data (joined journal/conference,
// all authors with role + hours, files, workflow metadata) into one .xlsx
// and streams it back as a Blob.
//
// FE props:
//   • rows      — already filtered rows (WorkflowFilterBar applied). We only
//                 need .id from each, but accepting the whole row keeps the
//                 disabled / "(N)" UI honest.
//   • workType  — string passed to BE, e.g. "scientificResearch", "patent".
//                 Must match the keys in service.WorkExportService.Export.

export const exportWorksToExcel = async ({ rows, workType }) => {
  const ids = (rows || []).map((r) => r.id).filter((id) => typeof id === "number" && id > 0);
  if (ids.length === 0) return { ok: false, message: "Không có công trình nào để xuất" };

  // Resolve endpoint + headers through API_URLS so any future auth header
  // change (Bearer token, etc.) flows through here automatically.
  const api = API_URLS.WORKS.exportWorks();
  const resp = await axios({
    method: api.method,
    url: api.endPoint,
    headers: api.headers,
    data: { workType, ids },
    responseType: "blob",
    // Don't throw on 4xx so we can surface the JSON error message.
    validateStatus: () => true,
  });

  if (resp.status !== 200) {
    let message = `Xuất Excel thất bại (HTTP ${resp.status})`;
    try {
      // Axios returned a Blob even on error; read it as text and parse JSON.
      const text = await resp.data.text();
      const json = JSON.parse(text);
      if (json && json.message) message = json.message;
    } catch (_) {
      // leave default message
    }
    return { ok: false, message };
  }

  // Filename: prefer the Content-Disposition the BE set (already includes a
  // timestamp + work-type prefix); fall back to a generic name if absent.
  let fileName = `Export_${workType}.xlsx`;
  const cd = resp.headers["content-disposition"] || resp.headers["Content-Disposition"];
  if (cd) {
    const m = /filename="?([^"]+)"?/i.exec(cd);
    if (m && m[1]) {
      try {
        fileName = decodeURIComponent(m[1]);
      } catch (_) {
        fileName = m[1];
      }
    }
  }
  saveAs(new Blob([resp.data]), fileName);
  return { ok: true };
};

export default function ExportExcelButton({
  rows,
  workType,
  disabled,
  size = "small",
  style,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onClick = async () => {
    if (busy || !workType) return;
    setBusy(true);
    setError("");
    const res = await exportWorksToExcel({ rows, workType });
    setBusy(false);
    if (!res.ok) setError(res.message);
  };

  return (
    <Button
      size={size}
      variant="outlined"
      color="primary"
      startIcon={busy ? <CircularProgress size={14} /> : <GetApp />}
      onClick={onClick}
      disabled={disabled || !rows || rows.length === 0 || !workType || busy}
      style={{ textTransform: "none", whiteSpace: "nowrap", ...(style || {}) }}
      title={error || `Xuất ${(rows || []).length} công trình ra Excel (đầy đủ thông tin)`}
    >
      {busy ? "Đang xuất…" : `Xuất Excel (${(rows || []).length})`}
    </Button>
  );
}
